// src/modules/wiring/hooks/useWireMarkerMove.js
//
// wireMarker (kablo üzerindeki eğik çizgi işaretleri) M tuşu ile KENDİ wire'ı üzerinde
// sürüklenebilsin. Paralel/yakın kablolarda marker'lar üst üste binebiliyor; kullanıcı M
// basılıyken marker'ı tutup wire boyunca boş bir yere kaydırabilir.
//
// Kısıt: marker wire'dan AYRILMAZ — mouse, marker'ın parent wire polyline'ına izdüşürülür
// (en yakın nokta). Marker'ın eğik çizgileri o noktadaki segment yönüne göre 45° sabit
// açıyla yeniden çizilir (wire açı yapınca marker da döner).
//
// Marker'lar useTesisatRebuild'den hariç (snapshot persist) → kaydırılan konum kayıt/açmada
// korunur. usePanelMove (M + panel) ile aynı M tuşunu paylaşır; hedef farklı (M + marker).

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { StateSnapshotCommand } from '../../undoRedo';

const HALF_LENGTH = 2.5;          // eğik çizgi yarı uzunluğu
const ANGLE_RAD = Math.PI / 4;    // 45° (wire'a göre sabit)

function rotate2(v, ang) {
  const c = Math.cos(ang), s = Math.sin(ang);
  return new THREE.Vector3(v.x * c - v.y * s, v.x * s + v.y * c, 0);
}

// Polyline üzerindeki en yakın noktayı + o segmentin yönünü bul.
function closestOnPolyline(worldVerts, p) {
  let bestPoint = null, bestDist = Infinity, bestDir = null;
  for (let i = 0; i < worldVerts.length - 1; i++) {
    const a = worldVerts[i], b = worldVerts[i + 1];
    const ab = new THREE.Vector3().subVectors(b, a);
    const len2 = ab.lengthSq();
    if (len2 === 0) continue;
    let t = new THREE.Vector3().subVectors(p, a).dot(ab) / len2;
    t = Math.max(0, Math.min(1, t));
    const proj = a.clone().add(ab.clone().multiplyScalar(t));
    const d = proj.distanceToSquared(p);
    if (d < bestDist) { bestDist = d; bestPoint = proj; bestDir = ab.clone().normalize(); }
  }
  return bestPoint ? { point: bestPoint, dir: bestDir } : null;
}

export default function useWireMarkerMove(scene, camera, domElement) {
  const dragRef = useRef(null);
  const mModeRef = useRef(false);

  useEffect(() => {
    if (!scene || !camera || !domElement) return;

    const ndcOf = (e) => {
      const r = domElement.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1
      );
    };
    const worldOf = (e) => new THREE.Vector3(ndcOf(e).x, ndcOf(e).y, 0).unproject(camera);

    const onKeyDown = (e) => { if (e.key === 'm' || e.key === 'M') mModeRef.current = true; };
    const onKeyUp = (e) => {
      if (e.key === 'm' || e.key === 'M') { mModeRef.current = false; dragRef.current = null; }
    };

    const resolveMarker = (obj) => {
      let n = obj;
      while (n) {
        if (n.userData?.role === 'wireMarker' && n.userData?.type === 'permanent') return n;
        n = n.parent;
      }
      return null;
    };

    const findWire = (wireId) => {
      if (!wireId) return null;
      let wire = null;
      scene.traverse((o) => {
        if (!wire && o.type === 'Line' && o.userData?.id === wireId) wire = o;
      });
      return wire;
    };

    const worldVertsOf = (wire) => {
      wire.updateWorldMatrix(true, false);
      const pos = wire.geometry?.attributes?.position;
      if (!pos) return [];
      const verts = [];
      for (let i = 0; i < pos.count; i++) {
        verts.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(wire.matrixWorld));
      }
      return verts;
    };

    // Marker'ı verilen nokta + segment yönüne göre yeniden çiz (45° sabit açı).
    const redrawMarker = (marker, point, dir) => {
      const lines = marker.children.filter((c) => c.isLine);
      const count = lines.length;
      if (count === 0) return;
      const fwdLen = count === 2 ? 1.5 : 3; // lighting 2 → 1.5, diğer → 3
      const forward = dir.clone().setLength(fwdLen);
      const angled = rotate2(dir, ANGLE_RAD);
      const centers = count === 2
        ? [point.clone().sub(forward), point.clone().add(forward)]
        : [point.clone().sub(forward), point.clone(), point.clone().add(forward)];
      centers.forEach((c, i) => {
        const line = lines[i];
        if (!line) return;
        const start = c.clone().add(angled.clone().multiplyScalar(-HALF_LENGTH));
        const end = c.clone().add(angled.clone().multiplyScalar(HALF_LENGTH));
        line.geometry.setFromPoints([start, end]);
        line.geometry.computeBoundingSphere?.();
      });
      // Gizmo + persistence tutarlılığı
      marker.userData.midPoint = point.clone();
      marker.userData.lastEdgeAngle = Math.atan2(dir.y, dir.x) + ANGLE_RAD;
    };

    const onDown = (e) => {
      if (!mModeRef.current || e.button !== 0) return;
      const rc = new THREE.Raycaster();
      rc.params.Line = { threshold: 5 };
      rc.setFromCamera(ndcOf(e), camera);
      const hits = rc.intersectObjects(scene.children, true);
      for (const h of hits) {
        if (h.object.visible === false) continue;
        const marker = resolveMarker(h.object);
        if (marker) {
          const wire = findWire(marker.userData?.parentWireId);
          const worldVerts = wire ? worldVertsOf(wire) : null;
          if (!worldVerts || worldVerts.length < 2) return; // wire yoksa sürükleme yok
          e.stopImmediatePropagation();
          e.preventDefault();
          const lines = marker.children.filter((c) => c.isLine);
          dragRef.current = { marker, worldVerts, beforeEntries: StateSnapshotCommand.capture(lines) };
          return;
        }
      }
    };

    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      e.stopImmediatePropagation();
      const mouseWorld = worldOf(e);
      const res = closestOnPolyline(d.worldVerts, mouseWorld);
      if (res) redrawMarker(d.marker, res.point, res.dir);
    };

    const onUp = () => {
      const d = dragRef.current;
      if (d?.beforeEntries) {
        const lines = d.marker.children.filter((c) => c.isLine);
        const after = StateSnapshotCommand.capture(lines);
        if (StateSnapshotCommand.changed(d.beforeEntries, after) && window.undoRedoManager) {
          const mgr = window.undoRedoManager;
          const tx = mgr.beginTransaction('Marker Taşı');
          mgr.addCommand(new StateSnapshotCommand(d.beforeEntries, after, 'MARKER_MOVE'));
          mgr.commitTransaction(tx);
        }
      }
      dragRef.current = null;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    domElement.addEventListener('mousedown', onDown, true);
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      domElement.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
  }, [scene, camera, domElement]);
}
